import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Squares2X2Icon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useWalletStore } from '../store/walletStore';
import { createMultiTokenCollectionOps, listTokens } from '../api/vexforge';
import { signAndSend, ensureConnected, EXPLORER_TX_URL } from '../lib/submit';

export default function MultiToken() {
  const { address, isConnected } = useWalletStore();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [submittedTx, setSubmittedTx] = useState(null);

  const [form, setForm] = useState({
    name: '',
    symbol: '',
    baseUri: '',
    royaltyBps: '500',
    description: '',
    iconUri: '',
    bannerUri: '',
    externalUrl: '',
  });

  useEffect(() => {
    async function fetchCollections() {
      try {
        const tokens = await listTokens(200);
        const mtCollections = (Array.isArray(tokens) ? tokens : []).filter(t => {
          const type = (t.token_type || t.type || '').toLowerCase();
          return type === 'multi' || type === 'vsc55' || type === 'multi-token';
        });
        setCollections(mtCollections);
      } catch (err) {
        console.error('Failed to fetch collections:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchCollections();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!isConnected) {
      await ensureConnected();
      return;
    }

    setCreating(true);
    try {
      const ops = createMultiTokenCollectionOps({
        name: form.name,
        symbol: form.symbol,
        baseUri: form.baseUri,
        royaltyBps: form.royaltyBps,
        owner: address,
        description: form.description,
        iconUri: form.iconUri,
        bannerUri: form.bannerUri,
        externalUrl: form.externalUrl,
      });
      const txHash = await signAndSend(ops, `Create multi-token collection ${form.symbol} (${form.name})`);
      if (!txHash) return;
      // Collection address is chain-derived at execution (includes the block
      // timestamp) — not knowable yet, so show the tx instead of redirecting.
      toast.success('Multi-token collection creation submitted!');
      setSubmittedTx(txHash);
      setShowCreate(false);
    } catch (err) {
      toast.error(err.message || 'Failed to create collection');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Multi-Token Studio</h1>
          <p className="text-gray-400 mt-1">Create and browse VSC-55 multi-token collections</p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center space-x-2 px-4 py-2 bg-gradient-vextaris text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
        >
          {showCreate ? <XMarkIcon className="w-5 h-5" /> : <PlusIcon className="w-5 h-5" />}
          <span>{showCreate ? 'Cancel' : 'Create Collection'}</span>
        </button>
      </div>

      {submittedTx && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-4 text-sm text-gray-300">
          <p className="text-green-400 font-medium mb-1">Collection creation submitted</p>
          <p>
            Transaction:{' '}
            <a href={`${EXPLORER_TX_URL}${submittedTx}`} target="_blank" rel="noopener noreferrer"
              className="font-mono text-vextaris-blue hover:text-white break-all">{submittedTx}</a>
          </p>
          <p className="text-gray-500 mt-1">
            The collection address is assigned by the chain when the transaction is included —
            it will appear in the list below (refresh) and on the explorer.
          </p>
        </div>
      )}

      {/* Create Form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6 space-y-4">
          <h2 className="text-xl font-semibold text-white">New Multi-Token Collection</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Collection Name</label>
              <input type="text" name="name" value={form.name} onChange={handleChange} required
                placeholder="Game Items" className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Symbol</label>
              <input type="text" name="symbol" value={form.symbol} onChange={handleChange} required maxLength={10}
                placeholder="ITEMS" className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple uppercase" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Base URI</label>
              <input type="text" name="baseUri" value={form.baseUri} onChange={handleChange} required
                placeholder="ipfs://Qm.../metadata/" className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple font-mono text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Royalty (basis points)</label>
              <input type="number" name="royaltyBps" value={form.royaltyBps} onChange={handleChange} min="0" max="10000"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white focus:outline-none focus:border-vextaris-purple" />
              <p className="text-xs text-gray-500 mt-1">500 = 5% royalty</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">External URL</label>
              <input type="url" name="externalUrl" value={form.externalUrl} onChange={handleChange}
                placeholder="https://mygame.com" className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} rows={2}
              placeholder="Describe your collection..." className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple" />
          </div>

          <button type="submit" disabled={creating}
            className="px-6 py-2 bg-gradient-vextaris text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50">
            {creating ? 'Creating...' : 'Create Collection'}
          </button>
        </form>
      )}

      {/* Collections Grid */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading collections...</div>
      ) : collections.length === 0 ? (
        <div className="bg-vextaris-dark/50 rounded-2xl border border-vextaris-purple/20 p-12 text-center">
          <Squares2X2Icon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400">No multi-token collections found yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {collections.map((col, i) => {
            const mint = col.mint_address || col.mint_address_hex || '';
            return (
              <Link
                key={i}
                to={`/multi-token/${mint}`}
                className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6 hover:border-vextaris-purple/40 transition-colors group"
              >
                <Squares2X2Icon className="w-10 h-10 text-vextaris-blue mb-3" />
                <h3 className="text-white font-semibold group-hover:text-vextaris-lime transition-colors">
                  {col.name || 'Unnamed Collection'}
                </h3>
                <p className="text-xs text-gray-400 font-mono mt-1">{col.symbol || '--'}</p>
                {col.royalty_bps !== undefined && (
                  <p className="text-xs text-gray-500 mt-2">{col.royalty_bps / 100}% royalty</p>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}